import React from 'react';
import styled from 'styled-components';
import { Button, Select } from '@monavate/ui-core';

const PaginationWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
`;

const PageSizeSelector = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${(props) => props.theme['Borders/active']};
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const PageButton = styled.button<{ active?: boolean }>`
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    border: 1px solid ${(props) => (props.active ? props.theme['Borders/quiet'] : 'transparent')};
    color: ${(props) => props.theme['Content/primary']};
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s;

    background: ${(props) => props.theme['Surface/page']};

    &:hover {
        ${(props) =>
            !props.active &&
            `
            border-color: ${props.theme['Borders/quiet']};
        `}
    }
`;

const Ellipsis = styled.span`
    font-size: 8px;
`;

interface CustomPaginationProps {
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    currentPageItemCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    pageSizeOptions?: number[];
    knownPages?: Set<number>;
    lastKnownPage?: number | null;
    enoughItemsForPagination?: boolean;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
    currentPage,
    pageSize,
    hasNextPage,
    currentPageItemCount,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 20, 50, 100],
    knownPages = new Set([1]),
    lastKnownPage = null,
    enoughItemsForPagination = true,
}) => {
    if (!enoughItemsForPagination) return null;

    // Only allow navigating forward to pages we've confirmed have data
    // (i.e. pages that the prefetch has actually returned). hasNextPage is the
    // hook's optimistic hint and can be true before the next page is confirmed.
    const canGoNext = hasNextPage && knownPages.has(currentPage + 1);

    const handlePrevious = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (canGoNext) {
            onPageChange(currentPage + 1);
        }
    };

    const renderPageButtons = () => {
        const buttons: React.ReactNode[] = [];

        // Determine the last page we can confirm exists. Never speculate beyond
        // confirmed knownPages — otherwise a slow prefetch would render
        // clickable page numbers for data we haven't actually fetched yet.
        let maxPage: number;
        if (currentPageItemCount < pageSize) {
            maxPage = currentPage;
        } else {
            maxPage = Math.max(currentPage, ...Array.from(knownPages));
        }

        // Sliding window: show 3 pages based on current page
        let startPage: number;
        if (currentPage <= 2) {
            startPage = 1;
        } else {
            startPage = currentPage - 1;
        }
        // Show up to 3 pages, but don't go beyond maxPage
        let lastRenderedPage = startPage;
        for (let i = 0; i < 3; i++) {
            const pageNum = startPage + i;
            if (pageNum <= maxPage) {
                buttons.push(
                    <PageButton
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => onPageChange(pageNum)}
                        data-test-id={`button-pagination-page-${pageNum}`}
                    >
                        {pageNum}
                    </PageButton>,
                );
                lastRenderedPage = pageNum;
            }
        }

        // Add ellipsis at the end if there might be more pages beyond what we're showing
        // Don't show ellipsis if lastKnownPage is already visible in the sliding window
        const shouldShowEllipsis =
            hasNextPage && (lastKnownPage === null || lastRenderedPage < lastKnownPage);
        if (shouldShowEllipsis) {
            buttons.push(
                <Ellipsis key="ellipsis-end">
                    <span className="material-symbols-outlined">more_horiz</span>
                </Ellipsis>,
            );
        }

        return buttons;
    };

    return (
        <PaginationWrapper>
            <PageSizeSelector>
                <Select
                    dataTestId="select-pagination-page-size"
                    value={pageSize}
                    showSearch={false}
                    onSelect={(e) => onPageSizeChange(Number(e))}
                    options={pageSizeOptions.map((option) => ({
                        value: option,
                        label: `${option} per page`,
                    }))}
                />
            </PageSizeSelector>

            <PaginationControls>
                <Button
                    dataTestId="button-pagination-previous"
                    disabled={currentPage === 1}
                    handleClick={handlePrevious}
                    viewType="tertiary"
                    style={{ fontWeight: 500 }}
                >
                    <span className="material-symbols-outlined">chevron_left</span>
                    Previous
                </Button>
                {renderPageButtons()}
                <Button
                    dataTestId="button-pagination-next"
                    disabled={!canGoNext}
                    handleClick={handleNext}
                    viewType="tertiary"
                    style={{ fontWeight: 500 }}
                >
                    Next
                    <span className="material-symbols-outlined">chevron_right</span>
                </Button>
            </PaginationControls>
        </PaginationWrapper>
    );
};

export default CustomPagination;
